import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Language } from "@/types/enums";
import type { I18nString } from "@/types/common";

interface I18nInputProps {
  label: string;
  value: I18nString;
  onChange: (value: I18nString) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  error?: string;
  required?: boolean;
}

const DEFAULT_I18N: I18nString = { ko: "", en: null };

export function I18nInput({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 3,
  error,
  required = false,
}: I18nInputProps) {
  const [lang, setLang] = useState<Language>(Language.KO);
  const safeValue = value ?? DEFAULT_I18N;

  const handleChange = (text: string, language: Language) => {
    if (language === Language.KO) {
      onChange({ ...safeValue, ko: text });
    } else {
      onChange({ ...safeValue, en: text || null });
    }
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className={error ? "text-destructive" : ""}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Tabs
          value={lang}
          onValueChange={(v) => setLang(v as Language)}
        >
          <TabsList className="h-7">
            <TabsTrigger value={Language.KO} className="text-xs px-2 py-0.5">
              KO
            </TabsTrigger>
            <TabsTrigger value={Language.EN} className="text-xs px-2 py-0.5">
              EN
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Tabs value={lang} onValueChange={(v) => setLang(v as Language)}>
        <TabsContent value={Language.KO} className="mt-0">
          <InputComponent
            value={safeValue.ko}
            onChange={(e) => handleChange(e.target.value, Language.KO)}
            placeholder={placeholder}
            {...(multiline ? { rows } : {})}
          />
        </TabsContent>
        <TabsContent value={Language.EN} className="mt-0">
          <InputComponent
            value={safeValue.en || ""}
            onChange={(e) => handleChange(e.target.value, Language.EN)}
            placeholder={`${placeholder || ""} (English, optional)`}
            {...(multiline ? { rows } : {})}
          />
        </TabsContent>
      </Tabs>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}
